import { quoteServiceAliases } from "@/data/quoteServices";

export interface QuoteWidgetOption {
  /** Stabilt id för valet */
  id: string;
  label: string;
  /** Alternativ etikett för företag/BRF (default: label) */
  businessLabel?: string;
  /** Tjänster (slugs) som valet motsvarar */
  services: string[];
  /** Tjänster när kunden är företag/BRF (default: services) */
  businessServices?: string[];
  /** Förifyllda svar i formuläret, nyckel = "{slug}.{fältId}" */
  prefill?: Record<string, string>;
  /** Förifyllda svar för företag/BRF (default: prefill) */
  businessPrefill?: Record<string, string>;
}

/**
 * Tjänstespecifika val i hero-widgeten. Istället för att lista alla tjänster
 * visar varje tjänstesida bara relevanta alternativ.
 */
export const quoteWidgetOptions: Record<string, QuoteWidgetOption[]> = {
  solceller: [
    {
      id: "solceller",
      label: "Solceller",
      services: ["solceller"],
      businessServices: ["kommersiella-solceller"],
    },
    {
      id: "solceller-batteri",
      label: "Solceller + batteri",
      services: ["solceller", "hembatterier"],
      businessServices: ["kommersiella-solceller", "kommersiella-batterier"],
      prefill: { "hembatterier.hasSolar": "Planerar att installera" },
      businessPrefill: { "kommersiella-batterier.hasSolar": "Planerar att installera" },
    },
  ],
  hembatterier: [
    {
      id: "batterier",
      label: "Batterier",
      businessLabel: "Batterilager",
      services: ["hembatterier"],
      businessServices: ["kommersiella-batterier"],
    },
    {
      id: "batterier-solceller",
      label: "Batterier + solceller",
      businessLabel: "Batterilager + solceller",
      services: ["hembatterier", "solceller"],
      businessServices: ["kommersiella-batterier", "kommersiella-solceller"],
      prefill: { "hembatterier.hasSolar": "Planerar att installera" },
      businessPrefill: { "kommersiella-batterier.hasSolar": "Planerar att installera" },
    },
  ],
  varmepumpar: [
    {
      id: "luft-vatten",
      label: "Luft/vatten",
      services: ["varmepumpar"],
      prefill: { "varmepumpar.pumpType": "Luft/vatten" },
    },
    {
      id: "bergvarme",
      label: "Bergvärme",
      services: ["varmepumpar"],
      prefill: { "varmepumpar.pumpType": "Bergvärme" },
    },
    {
      id: "franluft",
      label: "Frånluft",
      services: ["varmepumpar"],
      prefill: { "varmepumpar.pumpType": "Frånluft" },
    },
    {
      id: "luft-luft",
      label: "Luft/luft",
      services: ["varmepumpar"],
      prefill: { "varmepumpar.pumpType": "Luft/luft" },
      businessPrefill: { "varmepumpar.pumpType": "Vet ej – föreslå gärna" },
    },
    {
      id: "vet-ej",
      label: "Vet ej",
      services: ["varmepumpar"],
      prefill: { "varmepumpar.pumpType": "Vet ej – föreslå gärna" },
    },
  ],
  laddboxar: [
    { id: "laddbox", label: "Laddbox", businessLabel: "Laddboxar", services: ["laddboxar"] },
    { id: "laddstolpe", label: "Laddstolpe", businessLabel: "Laddstolpar", services: ["laddstolpar"] },
    { id: "vet-ej", label: "Vet ej", services: ["laddboxar"], businessServices: ["laddstolpar"] },
  ],
};

quoteWidgetOptions["laddstolpar"] = quoteWidgetOptions["laddboxar"]!;

export const getQuoteWidgetOptions = (slug?: string) => {
  if (!slug) return undefined;
  const resolved = quoteServiceAliases[slug] ?? slug;
  return quoteWidgetOptions[resolved];
};

/** Serialiserar förifyllda svar till en URL-parameter */
export const encodePrefill = (prefill?: Record<string, string>) =>
  prefill
    ? Object.entries(prefill)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("|")
    : "";

/** Läser förifyllda svar från URL-parametern */
export const decodePrefill = (value?: string | null): Record<string, string> => {
  if (!value) return {};
  const out: Record<string, string> = {};
  value.split("|").forEach((pair) => {
    const idx = pair.indexOf("=");
    if (idx > 0) out[pair.slice(0, idx)] = decodeURIComponent(pair.slice(idx + 1));
  });
  return out;
};
